import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { rankAlternativeVehicles } from '../utils/vehicleRecommendations';

const toDay = (date) => date
  ? moment.utc(date).startOf('day')
  : moment.invalid();

const mergeBookedPeriods = (periods) => {
  const sorted = periods
    .filter((period) => period?.from && period?.to)
    .map((period) => ({ from: toDay(period.from), to: toDay(period.to) }))
    .filter((period) => period.from.isValid() && period.to.isValid())
    .sort((a, b) => a.from.valueOf() - b.from.valueOf());

  return sorted.reduce((merged, period) => {
    const previous = merged[merged.length - 1];
    if (!previous || period.from.isAfter(previous.to.clone().add(1, 'day'))) {
      merged.push({ from: period.from.clone(), to: period.to.clone() });
    } else if (period.to.isAfter(previous.to)) {
      previous.to = period.to.clone();
    }
    return merged;
  }, []);
};

const findAvailablePeriods = (rangeStart, rangeEnd, bookedPeriods) => {
  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeEnd.isBefore(rangeStart)) {
    return [];
  }

  const available = [];
  let cursor = rangeStart.clone();

  bookedPeriods.forEach((booked) => {
    if (booked.to.isBefore(rangeStart) || booked.from.isAfter(rangeEnd)) return;

    const blockedStart = moment.max(booked.from, rangeStart);
    const blockedEnd = moment.min(booked.to, rangeEnd);

    if (blockedStart.isAfter(cursor)) {
      available.push({
        from: cursor.clone(),
        to: blockedStart.clone().subtract(1, 'day'),
      });
    }

    if (!blockedEnd.isBefore(cursor)) {
      cursor = blockedEnd.clone().add(1, 'day');
    }
  });

  if (!cursor.isAfter(rangeEnd)) {
    available.push({ from: cursor, to: rangeEnd.clone() });
  }

  return available;
};

const AvailabilityModal = ({
  isOpen,
  onClose,
  car,
  selectedStartDate,
  selectedEndDate,
  onDatesSelected,
  alternativeCars = [],
  requestedRateType = 'limited',
  getCarImg,
  onAlternativeSelected,
}) => {
  const [activePeriodKey, setActivePeriodKey] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [isApplyingDates, setIsApplyingDates] = useState(false);
  const [selectingAlternativeId, setSelectingAlternativeId] = useState(null);
  const [alternativeError, setAlternativeError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setActivePeriodKey(null);
      setDateSelectionError('');
      setIsApplyingDates(false);
      setSelectingAlternativeId(null);
      setAlternativeError('');
    }
  }, [isOpen, car?._id]);

  if (!isOpen || !car) return null;

  const selectedStart = toDay(selectedStartDate);
  const selectedEnd = toDay(selectedEndDate);
  const hasValidSelection = selectedStart.isValid()
    && selectedEnd.isValid()
    && !selectedEnd.isBefore(selectedStart);
  const formatDate = (date) => date.format('D MMM YYYY');
  const formatInputDate = (date) => date.format('YYYY-MM-DD');
  const requestedDurationDays = hasValidSelection
    ? Math.max(1, selectedEnd.diff(selectedStart, 'days'))
    : 1;
  const bookedPeriods = mergeBookedPeriods(car.fullyBookedPeriods || []);

  // Show the selected range plus the rest of every calendar month it touches.
  // Example: 30 June - 7 July searches through 31 July, so July gaps are visible.
  const displayStart = selectedStart.clone();
  const displayEnd = selectedEnd.clone().endOf('month');
  let availablePeriods = hasValidSelection
    ? findAvailablePeriods(displayStart, displayEnd, bookedPeriods)
    : [];
  let periodsHeading = hasValidSelection
    ? `Available periods through ${displayEnd.format('MMMM YYYY')}`
    : 'Available periods';
  let isShowingNextMonth = false;

  // The final month touched by the selection decides whether we advance.
  // For a June-to-July selection, a free June day must not prevent an
  // August fallback when July itself is completely booked.
  const finalMonthStart = selectedStart.isSame(selectedEnd, 'month')
    ? selectedStart.clone()
    : selectedEnd.clone().startOf('month');
  const finalMonthAvailability = hasValidSelection
    ? findAvailablePeriods(finalMonthStart, displayEnd, bookedPeriods)
    : [];

  // If the final selected month is full, find the first later month with free dates.
  if (hasValidSelection && finalMonthAvailability.length === 0) {
    availablePeriods = [];
    const searchLimit = selectedEnd.clone().add(12, 'months').endOf('month');
    let monthStart = displayEnd.clone().add(1, 'day').startOf('month');

    while (!monthStart.isAfter(searchLimit)) {
      const monthEnd = moment.min(monthStart.clone().endOf('month'), searchLimit);
      const monthAvailability = findAvailablePeriods(monthStart, monthEnd, bookedPeriods);

      if (monthAvailability.length > 0) {
        availablePeriods = monthAvailability;
        periodsHeading = `Next available periods in ${monthStart.format('MMMM YYYY')}`;
        isShowingNextMonth = true;
        break;
      }

      monthStart = monthStart.clone().add(1, 'month').startOf('month');
    }
  }

  const getPeriodKey = (period) => `${period.from.valueOf()}-${period.to.valueOf()}`;
  const bestChoicePeriod = availablePeriods
    .filter((period) => period.to.diff(period.from, 'days') >= requestedDurationDays)
    .map((period) => {
      const latestPossibleStart = period.to.clone().subtract(requestedDurationDays, 'days');
      const closestStart = moment.max(period.from, moment.min(selectedStart, latestPossibleStart));
      return {
        period,
        distanceFromRequestedStart: Math.abs(closestStart.diff(selectedStart, 'days')),
      };
    })
    .sort((a, b) => (
      a.distanceFromRequestedStart - b.distanceFromRequestedStart
      || a.period.from.valueOf() - b.period.from.valueOf()
    ))[0]?.period;
  const bestChoicePeriodKey = bestChoicePeriod ? getPeriodKey(bestChoicePeriod) : null;
  const recommendedVehicles = rankAlternativeVehicles(
    car,
    alternativeCars,
    requestedRateType
  );

  const selectAlternative = async (recommendation) => {
    if (typeof onAlternativeSelected !== 'function') return;
    setSelectingAlternativeId(recommendation.car._id);
    setAlternativeError('');
    try {
      const selected = await onAlternativeSelected(
        recommendation.car,
        requestedRateType
      );
      if (selected === false) {
        setAlternativeError('This vehicle has just become unavailable. Please choose another option.');
        setSelectingAlternativeId(null);
      }
    } catch (error) {
      setAlternativeError('We could not confirm this vehicle. Please try another option.');
      setSelectingAlternativeId(null);
    }
  };

  const openDateChooser = (period, periodKey) => {
    const preferredEnd = period.from.clone().add(requestedDurationDays, 'days');
    const initialEnd = moment.min(preferredEnd, period.to);
    const requestedDatesFit = !preferredEnd.isAfter(period.to);

    setActivePeriodKey(periodKey);
    setDraftStartDate(formatInputDate(period.from));
    setDraftEndDate(formatInputDate(initialEnd));
    setDateSelectionError(requestedDatesFit
      ? ''
      : `Your original ${requestedDurationDays}-day rental does not fit in this period. Choose a shorter stay or another available period.`);
  };

  const updateDraftStartDate = (value, period) => {
    if (!value) {
      setDraftStartDate('');
      setDraftEndDate('');
      setDateSelectionError('Select a pickup date.');
      return;
    }

    const nextStart = toDay(value);
    const preferredEnd = nextStart.clone().add(requestedDurationDays, 'days');
    const nextEnd = moment.min(preferredEnd, period.to);

    setDraftStartDate(value);
    setDraftEndDate(formatInputDate(nextEnd));
    setDateSelectionError(preferredEnd.isAfter(period.to)
      ? `Only ${period.to.diff(nextStart, 'days') + 1} days are available from this pickup date.`
      : '');
  };

  const applySelectedDates = async (period) => {
    const nextStart = toDay(draftStartDate);
    const nextEnd = toDay(draftEndDate);

    if (!nextStart.isValid() || !nextEnd.isValid()) {
      setDateSelectionError('Select both pickup and return dates.');
      return;
    }

    if (nextStart.isBefore(period.from) || nextEnd.isAfter(period.to) || nextEnd.isBefore(nextStart)) {
      setDateSelectionError('Choose dates inside this available period.');
      return;
    }

    if (typeof onDatesSelected !== 'function') return;

    setIsApplyingDates(true);
    setDateSelectionError('');
    try {
      await onDatesSelected({
        startDate: formatInputDate(nextStart),
        endDate: formatInputDate(nextEnd),
      });
    } catch (error) {
      setIsApplyingDates(false);
      setDateSelectionError('The dates could not be updated. Please try again.');
    }
  };

  return (
    <div className="availability-modal-overlay" onClick={onClose}>
      <div
        className="availability-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="availability-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="availability-modal-header">
          <div className="availability-modal-header-copy">
            <h3 id="availability-modal-title">
              {car.name || `${car.brand} ${car.model}`} Availability
            </h3>
            <div className="availability-modal-description">Choose new dates or a similar available vehicle</div>
          </div>
          <button
            type="button"
            className="availability-modal-close"
            aria-label="Close availability"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="availability-modal-body">
          {hasValidSelection && (
            <div className="availability-selected-dates">
              <span>Your selected dates</span>
              <strong>{formatDate(selectedStart)} &mdash; {formatDate(selectedEnd)}</strong>
            </div>
          )}

          <div className="availability-status-message">
            <strong>Unavailable for your selected dates</strong>
            <span>Here are the nearest periods when this vehicle can be rented.</span>
          </div>

          {isShowingNextMonth && (
            <div className="availability-next-month-message">
              The final month in your selected range is fully booked, so the next available month is shown.
            </div>
          )}

          {availablePeriods.length > 0 ? (
            <>
              <div className="availability-periods-heading">{periodsHeading}</div>
              <div className="availability-periods">
                {availablePeriods.map((period, index) => {
                  const availableDays = period.to.diff(period.from, 'days') + 1;
                  const periodKey = getPeriodKey(period);
                  const isActivePeriod = activePeriodKey === periodKey;
                  const isBestChoice = bestChoicePeriodKey === periodKey;
                  return (
                    <div
                      key={`${periodKey}-${index}`}
                      className={`availability-period-card ${isActivePeriod ? 'is-selecting' : ''}`}
                    >
                      <div className="availability-period-info">
                        <div className="availability-period-summary">
                          <div className="availability-period-date-group">
                            <span className="availability-period-dates">
                              {formatDate(period.from)} &mdash; {formatDate(period.to)}
                            </span>
                            {isBestChoice && (
                              <span className="availability-best-choice">Best choice</span>
                            )}
                          </div>
                          <span className="availability-period-days">
                            {availableDays} {availableDays === 1 ? 'day' : 'days'} available
                          </span>
                        </div>
                        <button
                          type="button"
                          className="availability-choose-dates"
                          onClick={() => isActivePeriod
                            ? setActivePeriodKey(null)
                            : openDateChooser(period, periodKey)}
                        >
                          {isActivePeriod ? 'Cancel' : 'Choose dates'}
                        </button>
                      </div>

                      {isActivePeriod && (
                        <div className="availability-date-chooser">
                          <div className="availability-date-fields">
                            <label>
                              <span>Pickup date</span>
                              <input
                                type="date"
                                min={formatInputDate(period.from)}
                                max={formatInputDate(period.to)}
                                value={draftStartDate}
                                onChange={(event) => updateDraftStartDate(event.target.value, period)}
                              />
                            </label>
                            <label>
                              <span>Return date</span>
                              <input
                                type="date"
                                min={draftStartDate || formatInputDate(period.from)}
                                max={formatInputDate(period.to)}
                                value={draftEndDate}
                                onChange={(event) => {
                                  setDraftEndDate(event.target.value);
                                  setDateSelectionError('');
                                }}
                              />
                            </label>
                          </div>

                          {dateSelectionError && (
                            <div className="availability-date-error">{dateSelectionError}</div>
                          )}

                          <button
                            type="button"
                            className="availability-apply-dates"
                            disabled={isApplyingDates}
                            onClick={() => applySelectedDates(period)}
                          >
                            {isApplyingDates ? 'Updating dates...' : 'Apply these dates'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="availability-empty">
              No available periods were found. Please contact us and we will help find another date or vehicle.
            </div>
          )}

          <section className="availability-alternatives" aria-labelledby="availability-alternatives-title">
            <div className="availability-alternatives-header">
              <div>
                <h4 id="availability-alternatives-title">Available for your original dates</h4>
                <p>Best alternatives based on seats, luggage space, transmission and price.</p>
              </div>
              <span>{recommendedVehicles.length} found</span>
            </div>

            {recommendedVehicles.length > 0 ? (
              <div className="availability-alternative-list">
                {recommendedVehicles.map((recommendation, index) => {
                  const alternative = recommendation.car;
                  const image = typeof getCarImg === 'function' ? getCarImg(alternative) : null;
                  const isSelecting = selectingAlternativeId === alternative._id;
                  return (
                    <article className="availability-alternative-card" key={alternative._id}>
                      <div className="availability-alternative-image">
                        {image ? (
                          <img src={image} alt={alternative.name || 'Alternative vehicle'} />
                        ) : (
                          <span aria-hidden="true">🚗</span>
                        )}
                      </div>
                      <div className="availability-alternative-copy">
                        <div className="availability-alternative-name-row">
                          <strong>{alternative.name || `${alternative.brand} ${alternative.model}`}</strong>
                          {index === 0 && <span className="availability-alternative-best">Best match</span>}
                        </div>
                        <div className="availability-alternative-reasons">
                          {recommendation.reasons.join(' • ')}
                        </div>
                        <div className="availability-alternative-rate">
                          ${recommendation.rate.toFixed(2)} per day
                          <span>{requestedRateType === 'unlimited' ? 'Unlimited KM' : '150 KM / day'}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="availability-select-alternative"
                        disabled={Boolean(selectingAlternativeId)}
                        onClick={() => selectAlternative(recommendation)}
                      >
                        {isSelecting ? 'Checking...' : 'Select'}
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="availability-alternatives-empty">
                No other vehicles are currently available for the selected dates.
              </div>
            )}

            {alternativeError && (
              <div className="availability-alternative-error">{alternativeError}</div>
            )}
          </section>
        </div>

        <div className="availability-modal-footer">
          <div className="availability-contact-text">
            Need help? Contact us to find another date or vehicle.
          </div>
          <div className="availability-contact-buttons">
            <a href="tel:+61409521554" className="availability-btn-call">
              <i className="fas fa-mobile-alt"></i> +61 409 521 554
            </a>
            <a href="mailto:bookings@tak8.com.au?subject=Vehicle Availability Enquiry" className="availability-btn-email">
              <i className="fas fa-envelope"></i> bookings@tak8.com.au
            </a>
          </div>
          <button type="button" className="availability-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
